import { Box, Divider, Grid, useTheme } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import {
  EditButton,
  FunctionField,
  Labeled,
  ReferenceField,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
  TitleProps,
  useDataProvider,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { NotificationConditionalContentDto } from '../../../core/dto/system-notifications/notification-conditional-content.dto'
import { NotificationCategoryDto } from '../../../core/dto/system-notifications/notification-category.dto'
import { ExtendedTheme } from '../../layout/app.theme'
import { NotificationTagDto } from '../../../core/dto/system-notifications/notification-tag.dto'

interface NotificationConditionalContentTitleProps extends TitleProps {
  record?: NotificationConditionalContentDto
}

const ShowTitle = (data: NotificationConditionalContentTitleProps) => {
  const { record: content } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.notification-conditional-contents.titles.show')}:{' '}
      {content?.id}. {content?.description}
    </span>
  )
}

const GeneralActions = () => {
  const { record: content } =
    useShowContext<NotificationConditionalContentDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item>
            <EditButton
              basePath={`/${ResourceName.NOTIFICATION_CONDITIONAL_CONTENTS}`}
              record={content}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={
                !hasAuthority(Authority.EDIT_NOTIFICATION_CONDITIONAL_CONTENTS)
              }
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const NotificationConditionalContentTabs = ({ ...props }) => {
  const theme = useTheme<ExtendedTheme>()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const { record: notificationConditionalContent } = useShowContext()

  const canViewItems = hasAuthority(Authority.VIEW_NOTIFICATION_TAGS)

  const dataProvider = useDataProvider()
  const [relatedTags, setRelatedTags] = useState<
    NotificationTagDto[] | undefined
  >(undefined)

  const refetchItems = useCallback(() => {
    if (!canViewItems) {
      // do nothing for unauthorized users
      return Promise.resolve({ data: [], total: 0 })
    }

    return dataProvider
      .getList(ResourceName.NOTIFICATION_TAGS, {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          categoryId: notificationConditionalContent?.notificationCategoryId,
        },
      })
      .then((response) => {
        if (response) setRelatedTags(response.data as any)
        return response
      })
      .catch((err) => {
        throw err
      })
  }, [dataProvider, canViewItems, notificationConditionalContent])

  useEffect(() => {
    if (canViewItems) {
      refetchItems().catch((err) => {
        throw err
      })
    } else {
      setRelatedTags(undefined)
    }
  }, [canViewItems, refetchItems])

  useEffect(() => {
    if (canViewItems) {
      refetchItems()
    } else {
      setRelatedTags(undefined)
    }
  }, [canViewItems, refetchItems])

  return (
    <TabbedShowLayout {...props}>
      <Tab label="resources.notification-conditional-contents.tabs.general">
        <TextField label="ID" source="id" />
        <TextField source="description" />
        <TextField source="jsonStatusPropertyPath" />
        <FunctionField<NotificationConditionalContentDto>
          source="jsonStatusPropertyComparisonType"
          render={(record?: NotificationConditionalContentDto) => (
            <div>
              {translate(
                `resources.enums.jsonStatusPropertyComparisonTypes.${record?.jsonStatusPropertyComparisonType}`,
              )}
            </div>
          )}
        />
        <TextField source="comparisonValue" />
        {hasAuthority(Authority.VIEW_NOTIFICATION_TAGS) && (
          <ReferenceField
            source="notificationCategoryId"
            reference={ResourceName.NOTIFICATION_CATEGORIES}
            link={false}
          >
            <FunctionField<NotificationCategoryDto>
              source="name"
              render={(category?: NotificationCategoryDto) =>
                `${category?.id}. ${category?.defaultTitle}`
              }
            />
          </ReferenceField>
        )}
        <GeneralActions />
      </Tab>
      {hasAuthority(Authority.VIEW_NOTIFICATION_TAGS) && (
        <Tab
          label="resources.notification-conditional-contents.tabs.notification-tags"
          path="notification-tags"
          disabled={!relatedTags?.length}
        >
          <Box p={2}>
            {relatedTags &&
              relatedTags.map((tag: any) => (
                <Box
                  key={tag.id}
                  mb={2}
                  p={2}
                  border={`1px solid ${theme.palette.divider}`}
                  borderRadius={theme.shape.borderRadius}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={2}>
                      <Labeled label="ID">
                        <ReferenceField
                          source="id"
                          record={tag}
                          reference={ResourceName.NOTIFICATION_TAGS}
                          link="show"
                        >
                          <TextField source="id" />
                        </ReferenceField>
                      </Labeled>
                    </Grid>
                    <Grid item xs={12} sm={10}>
                      <Labeled
                        label={translate(
                          'resources.notification-tags.fields.description',
                        )}
                      >
                        <TextField record={tag} source="description" />
                      </Labeled>
                    </Grid>
                  </Grid>
                </Box>
              ))}
          </Box>
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const NotificationConditionalContentShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <NotificationConditionalContentTabs />
  </Show>
)
